import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject } from 'rxjs';

export interface Language {
  code: string;
  name: string;
  flag: string;
}

@Injectable({
  providedIn: 'root'
})
export class LanguageService {
  private readonly STORAGE_KEY = 'amplify_language';
  
  readonly availableLanguages: Language[] = [
    { code: 'en', name: 'English', flag: '🇬🇧' },
    { code: 'de', name: 'Deutsch', flag: '🇩🇪' }
  ];

  private currentLanguageSubject = new BehaviorSubject<Language>(this.availableLanguages[0]);
  currentLanguage$ = this.currentLanguageSubject.asObservable();

  constructor(private translate: TranslateService) {
    this.initializeLanguage();
  }

  private initializeLanguage(): void {
    // Set available languages
    this.translate.addLangs(this.availableLanguages.map(l => l.code));
    this.translate.setDefaultLang('en');

    // Try to get saved language from localStorage
    const savedLang = localStorage.getItem(this.STORAGE_KEY);
    const browserLang = this.translate.getBrowserLang();
    
    let langToUse = 'en';
    
    if (savedLang && this.isValidLanguage(savedLang)) {
      langToUse = savedLang;
    } else if (browserLang && this.isValidLanguage(browserLang)) {
      langToUse = browserLang;
    }

    this.setLanguage(langToUse);
  }

  private isValidLanguage(code: string): boolean {
    return this.availableLanguages.some(l => l.code === code);
  }

  setLanguage(code: string): void {
    if (!this.isValidLanguage(code)) {
      console.warn(`Language '${code}' is not available. Using default.`);
      code = 'en';
    }

    this.translate.use(code);
    localStorage.setItem(this.STORAGE_KEY, code);
    
    const language = this.availableLanguages.find(l => l.code === code)!;
    this.currentLanguageSubject.next(language);
  }

  getCurrentLanguage(): Language {
    return this.currentLanguageSubject.value;
  }

  getCurrentLanguageCode(): string {
    return this.currentLanguageSubject.value.code;
  }
}
